import React from "react";
import { Alert, Box, Typography } from "@mui/material";
import ChatMessage from "./ChatMessage";
import ChatComposer from "./ChatComposer";
import ChatHeader from "./ChatHeader";
import { STEP_CRM, STEP_NAME } from "../onboardingShared";

const TOP_GRADIENT =
  "linear-gradient(115deg, #F6F4FF 0%, rgba(74,44,245,0.16) 28%, rgba(232,74,138,0.12) 52%, #F6F4FF 72%, rgba(246,244,255,0) 100%)";

function OnboardingChatView({
  hasConversation, busy, completionPending, handleBack,
  onSkip, progressLabel, threadRef, messages,
  latestAppId, openingQuestion, step, nameSuggestion,
  otherMode, question, activeSuggestions, pendingSelection,
  handleToggleSuggestion, handleSubmitText, listening, handleToggleVoice,
  draft, setDraft, crmError
}) {
  return (
    <Box
      data-testid="onboarding-chat"
      data-chat-mode={hasConversation ? "thread" : "hero"}
      sx={{
        position: "relative",
        minHeight: "100vh",
        display: "flex",
        flexDirection: "column",
        bgcolor: "#FFFFFF",
        overflow: "hidden",
      }}
    >
      <Box
        aria-hidden="true"
        data-testid="onboarding-top-gradient"
        sx={{
          position: "absolute",
          top: 0,
          left: 0,
          right: 0,
          height: { xs: 64, md: 80 },
          pointerEvents: "none",
          zIndex: 0,
          backgroundImage: TOP_GRADIENT,
          backgroundSize: "220% 100%",
          backgroundRepeat: "no-repeat",
          animation: "onboardingGradientDrift 14s ease-in-out infinite",
          "@keyframes onboardingGradientDrift": {
            "0%, 100%": { backgroundPosition: "0% 50%" },
            "50%": { backgroundPosition: "100% 50%" },
          },
          maskImage: "linear-gradient(180deg, #000 20%, transparent 100%)",
          WebkitMaskImage: "linear-gradient(180deg, #000 20%, transparent 100%)",
          "@media (prefers-reduced-motion: reduce)": {
            animation: "none",
            backgroundPosition: "40% 50%",
          },
        }}
      />

      {/* Soft center glow — fades out as the conversation starts */}
      <Box
        aria-hidden="true"
        sx={{
          position: "absolute",
          top: "44%",
          left: "50%",
          width: { xs: 340, md: 560 },
          height: { xs: 240, md: 320 },
          transform: "translate(-50%, -50%)",
          pointerEvents: "none",
          zIndex: 0,
          opacity: hasConversation ? 0 : 1,
          transition: "opacity 480ms cubic-bezier(0.22, 1, 0.36, 1)",
          background:
            "radial-gradient(ellipse at center, rgba(74,44,245,0.13) 0%, rgba(246,244,255,0.5) 40%, rgba(255,255,255,0) 72%)",
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        }}
      />

      <ChatHeader
        canGoBack={hasConversation}
        backDisabled={busy || completionPending}
        skipDisabled={completionPending}
        onBack={handleBack}
        onSkip={onSkip}
        progressLabel={progressLabel}
      />

      {/*
        Single stage: hero centers question + composer as one composition.
        After the first answer, the composer stays mounted and docks to 40px
        from the bottom while the thread fills the space above.
      */}
      <Box
        data-testid={hasConversation ? "onboarding-chat-thread-stage" : "onboarding-chat-hero"}
        sx={{
          position: "relative",
          zIndex: 1,
          flex: 1,
          minHeight: 0,
          display: "flex",
          flexDirection: "column",
          justifyContent: hasConversation ? "flex-end" : "center",
          transition: "justify-content 520ms cubic-bezier(0.22, 1, 0.36, 1)",
          "@media (prefers-reduced-motion: reduce)": {
            transition: "none",
          },
        }}
      >
        {hasConversation ? (
          <Box
            ref={threadRef}
            data-testid="onboarding-chat-thread"
            sx={{
              flex: 1,
              minHeight: 0,
              overflowY: "auto",
              px: { xs: 2.5, md: 4 },
              pt: { xs: 1.5, md: 2 },
              pb: 1,
              animation: "onboardingThreadReveal 480ms cubic-bezier(0.22, 1, 0.36, 1)",
              "@keyframes onboardingThreadReveal": {
                from: { opacity: 0, transform: "translateY(12px)" },
                to: { opacity: 1, transform: "translateY(0)" },
              },
              "@media (prefers-reduced-motion: reduce)": {
                animation: "none",
              },
            }}
          >
            <Box
              sx={{
                width: "100%",
                maxWidth: 560,
                mx: "auto",
                display: "flex",
                flexDirection: "column",
                justifyContent: "flex-end",
                minHeight: "100%",
                py: 1,
              }}
            >
              {messages.map((message) => {
                const isLatestApp =
                  message.role === "app" && message.id === latestAppId;
                return (
                  <ChatMessage
                    key={message.id}
                    role={message.role}
                    variant="chat"
                    isLatest={isLatestApp}
                  >
                    {message.text}
                  </ChatMessage>
                );
              })}
            </Box>
          </Box>
        ) : (
          <Box
            sx={{
              width: "100%",
              maxWidth: 580,
              mx: "auto",
              px: { xs: 2.5, md: 4 },
              // Question → chips breathing room
              mb: { xs: 3.5, md: 4.5 },
            }}
          >
            <ChatMessage role="app" variant="hero">
              {openingQuestion}
            </ChatMessage>
          </Box>
        )}

        <Box
          data-testid="onboarding-chat-dock"
          sx={{
            flexShrink: 0,
            width: "100%",
            maxWidth: hasConversation ? 560 : 580,
            mx: "auto",
            px: { xs: 2.5, md: 4 },
            pt: hasConversation ? 1.25 : 0,
            // Docked: 40px from bottom. Hero: sit naturally in the centered stack.
            pb: hasConversation
              ? "40px"
              : { xs: 5, md: 7 },
            background: hasConversation
              ? "linear-gradient(180deg, rgba(255,255,255,0) 0%, #FFFFFF 22%)"
              : "transparent",
            transition:
              "padding 520ms cubic-bezier(0.22, 1, 0.36, 1), max-width 520ms cubic-bezier(0.22, 1, 0.36, 1), background 420ms ease",
            animation: hasConversation
              ? "onboardingComposerDock 520ms cubic-bezier(0.22, 1, 0.36, 1)"
              : "none",
            "@keyframes onboardingComposerDock": {
              from: { transform: "translateY(-28px)", opacity: 0.92 },
              to: { transform: "translateY(0)", opacity: 1 },
            },
            "@media (prefers-reduced-motion: reduce)": {
              transition: "none",
              animation: "none",
            },
          }}
        >
          {step === STEP_CRM && <Typography variant="body2" sx={{ mb: 1.5, color: "#58595B" }}>{question.description}</Typography>}
          {step === STEP_CRM && crmError && <Alert severity="error" sx={{ mb: 1.5 }}>{crmError}</Alert>}
          <ChatComposer
            placeholder={
              step === STEP_NAME
                ? `i.e. ${nameSuggestion}`
                : step === STEP_CRM && pendingSelection.includes("Other")
                  ? question.otherPlaceholder
                : otherMode
                  ? "Search industries…"
                  : question.placeholder
            }
            suggestions={activeSuggestions}
            selection={otherMode ? "multiple" : question.selection}
            pendingSelection={pendingSelection}
            onToggleSuggestion={handleToggleSuggestion}
            onSubmit={handleSubmitText}
            disabled={busy || completionPending}
            requireDraft={step === STEP_CRM && pendingSelection.includes("Other")}
            preserveDraftOnSubmit={step === STEP_CRM}
            listening={listening}
            onToggleVoice={handleToggleVoice}
            draftValue={draft}
            onDraftChange={setDraft}
            layout={hasConversation ? "docked" : "hero"}
          />
        </Box>
      </Box>
    </Box>
  );
}

export { OnboardingChatView, TOP_GRADIENT };
